'use client';

import { AccountCard } from '@eliadmin/core/src/components/nt/account/shell/account-card';
import { ProfileTextField } from '@eliadmin/core/src/components/nt/account/profile/profile-fields';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CheckoutTexts } from '@eliadmin/core/src/utils/nt/checkout';
import { cleanHref } from '@eliadmin/core/src/utils/nt/text';

/**
 * Figma „Chip“ (205:11129): #F5F6F3 pill, 14 px green icon, Body/M Semibold – „Fyzická osoba“,
 * for a company customer the checkout's „Firma / podnikateľ“ (the profile frame shows a person).
 */
function AccountTypeChip({ company }: { company: string | null }) {
  const { t } = useTranslations();

  return (
    <p className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#F5F6F3] py-1.5 pl-2.5 pr-3">
      <Icon name={company ? 'buildings' : 'user'} size={14} className="text-[#127A26]" />
      <span className="nt-body-m-semibold text-[#0D110D]">{company ?? t('text2234')}</span>
    </p>
  );
}

/**
 * Figma „Note“ (205:11129): #F5F6F3 box, radius 12, padding 14 / 16, the envelope, the e-mail
 * change text (Body/M #363D36) and „Kontaktovať podporu“ (UI/Button M #0E6A20, underlined) – on
 * the right in a wide column, under the text in a narrow one.
 */
function EmailChangeNote() {
  const texts = useAccountTexts();
  const href = cleanHref(texts.contactSupportHref);
  if (!texts.emailChange) return null;

  return (
    <div className="flex items-start gap-3 rounded-xl bg-[#F5F6F3] px-4 py-3.5 @[48rem]:items-center">
      <Icon name="envelope-simple" size={20} className="text-[#127A26]" />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2 @[48rem]:flex-row @[48rem]:items-center @[48rem]:justify-between @[48rem]:gap-3">
        <p className="nt-body-m text-[#363D36]">{texts.emailChange}</p>
        {href && texts.contactSupport ? (
          <Link
            href={href}
            className="nt-button-m shrink-0 rounded text-[#0E6A20] underline underline-offset-2 transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none"
          >
            {texts.contactSupport}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Figma „Card · Osobné údaje“ (205:11129): the account type, name and surname, the login e-mail
 * (read-only – it changes on request only, hence the note) and the phone, two per row in a wide
 * column.
 */
export function PersonalCard({
  isCompany,
  checkoutTexts,
}: {
  isCompany: boolean;
  checkoutTexts: CheckoutTexts;
}) {
  const { t } = useTranslations();
  const texts = useAccountTexts();

  return (
    <AccountCard
      title={t('text198')}
      aside={<AccountTypeChip company={isCompany ? checkoutTexts.company : null} />}
    >
      <div className="grid items-start gap-4 @[36rem]:grid-cols-2">
        <ProfileTextField name="name" label={t('text33')} required autoComplete="given-name" />
        <ProfileTextField name="surname" label={t('text34')} required autoComplete="family-name" />
        <ProfileTextField
          name="email"
          type="email"
          label={t('text35')}
          required
          readOnly
          aria-readonly
          hint={texts.emailHint || undefined}
          autoComplete="email"
        />
        <ProfileTextField
          name="phone"
          type="tel"
          inputMode="tel"
          label={t('text36')}
          required
          autoComplete="tel"
        />
      </div>
      <EmailChangeNote />
    </AccountCard>
  );
}
